/**
 * §M1 · `NavContext` re-scopeado a UNA organización concreta.
 *
 * `useNavContext` responde siempre por la org ACTIVA del switcher — perfecto
 * para pintar la barra lateral, inútil para «Mis perfiles», que tiene que
 * explicar las N academias a la vez sin obligar al usuario a cambiar de una en
 * una. Este hook reutiliza el contexto global (sesión, rol de plataforma,
 * docente, representante) y sustituye lo que SÍ es por-organización: el rol de
 * membresía y la resolución de features.
 *
 * Devuelve además `blockerOf`, la key del grafo que de verdad bloquea una
 * feature: puede ser un REQUISITO suyo, y decir «falta X» cuando el culpable es
 * su dependencia manda al usuario a pelear con el interruptor equivocado.
 */
import { type Accessor, createMemo } from "solid-js";
import { featureGraph, parseFeatureOverrides, resolveFeatureEffective } from "@space/shared";

import type { NavContext } from "@/features/navigation/nav-model";
import { useNavContext } from "@/features/navigation/use-nav-context";
import { useOrgSettings } from "@/features/academy/hooks/use-academy-data";
import { useMyOrgRole } from "@/features/academy/hooks/use-people-data";
import { usePlatformFlags } from "@/features/flags/use-feature-enabled";

export interface OrgNavContext {
  context: Accessor<NavContext>;
  /** Key del grafo que bloquea `key` (o `undefined` si está habilitada). */
  blockerOf: (key: string) => string | undefined;
}

export function useOrgNavContext(orgId: Accessor<string | undefined>): OrgNavContext {
  const base = useNavContext();
  const myRole = useMyOrgRole(orgId);
  const platformFlags = usePlatformFlags();
  const orgSettings = useOrgSettings(orgId);
  const graph = featureGraph();

  const resolver = createMemo(() => {
    const platformMap = platformFlags();
    const org = parseFeatureOverrides(orgSettings.data?.features);
    return (key: string) => resolveFeatureEffective(key, { platformMap, org }, graph);
  });

  const context = createMemo<NavContext>(() => ({
    ...base(),
    orgRole: myRole.role(),
    featureEnabled: (key: string) => resolver()(key).enabled,
  }));

  return { context, blockerOf: (key) => resolver()(key).blockedBy };
}
